import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaUsers, FaCheck, FaArrowRight } from 'react-icons/fa';
import './PanchKailash.css';

const PanchKailash = () => {
  const panchKailashTours = [
    {
      id: 'kinner-kailash',
      name: 'Kinner Kailash',
      duration: '03 Nights 04 Days',
      groupSize: '8-12 Group Size',
      price: '₹ 9,000/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754933925/utizq9hoac5ukuhbg9zw.jpg',
      highlights: [
        'Sacred Kinner Kailash peak',
        'Challenging trek',
        'Spiritual experience',
        'Mountain views'
      ],
      path: '/kinner-kailash-tour'
    },
    {
      id: 'shrikhand-mahadev',
      name: 'Shrikhand Mahadev',
      duration: '05 Nights 06 Days',
      groupSize: '8-12 Group Size',
      price: '₹ 12,999/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754933925/i0fekxzttd0vmevllkpc.jpg',
      highlights: [
        'Ancient Shrikhand Mahadev peak',
        'Difficult trek route',
        'Meditation experience',
        'Natural beauty'
      ],
      path: '/shrikhand-mahadev-tour'
    },
    {
      id: 'manimahesh-kailash',
      name: 'Manimahesh Kailash',
      duration: '03 Nights 04 Days',
      groupSize: '8-12 Group Size',
      price: '₹ 12,000/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754933925/ozp17l8cadag4n4aqfcv.webp',
      highlights: [
        'Sacred Manimahesh peak',
        'Panoramic views',
        'Moderate trek',
        'Spiritual journey'
      ],
      path: '/manimahesh-tour'
    },
    {
      id: 'adi-kailash',
      name: 'Adi Kailash',
      duration: '06 Nights 07 Days',
      groupSize: '8-12 Group Size',
      price: '₹ 17,999/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754933925/ulnzeroqhg5ecxgwkmpk.webp',
      highlights: [
        'Remote Adi Kailash peak',
        'Extended pilgrimage',
        'Challenging terrain',
        'Complete experience'
      ],
      path: '/adi-kailash-tour'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Panch Kailash - NomadTrailz</title>
        <meta name="description" content="Explore the sacred Panch Kailash pilgrimage sites - Kinner Kailash, Shrikhand Mahadev, Manimahesh Kailash, and Adi Kailash." />
      </Helmet>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        {/* Hero Section */}
        <section className="panch-kailash-hero">
          <div className="hero-background">
            <img src="https://res.cloudinary.com/duym2bgp2/image/upload/v1754933925/ulnzeroqhg5ecxgwkmpk.webp" alt="Panch Kailash" />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
            >
              Panch Kailash
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Choose your preferred Panch Kailash pilgrimage
            </motion.p>
          </div>
        </section>

        {/* Tours Section */}
        <section className="panch-kailash-tours">
          <div className="container">
            <div className="tours-grid">
              {panchKailashTours.map((tour, index) => (
                <motion.div
                  key={tour.id}
                  className="tour-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <div className="tour-image">
                    <img src={tour.image} alt={tour.name} />
                  </div>
                  <div className="tour-content">
                    <h3>{tour.name}</h3>
                    <div className="tour-details">
                      <div className="detail-item">
                        <FaCalendarAlt className="detail-icon" />
                        <span>{tour.duration}</span>
                      </div>
                      <div className="detail-item">
                        <FaUsers className="detail-icon" />
                        <span>{tour.groupSize}</span>
                      </div>
                    </div>
                    <div className="tour-price">
                      <span className="price-label">Starting From</span>
                      <span className="price-amount">{tour.price}</span>
                    </div>
                    <div className="tour-highlights">
                      {tour.highlights.map((highlight, idx) => (
                        <div key={idx} className="highlight-item">
                          {highlight}
                        </div>
                      ))}
                    </div>
                    <Link to={tour.path} className="view-details-btn">
                      View Details
                      <FaArrowRight className="btn-icon" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="panch-kailash-about">
          <div className="container">
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>About Panch Kailash</h2>
              <p>
                Panch Kailash refers to five sacred peaks associated with Lord Shiva in the Himalayas. 
                These peaks are considered to be the abodes of Lord Shiva and are revered by devotees 
                for their spiritual significance and natural beauty. The Panch Kailash pilgrimage is 
                one of the most challenging and spiritually rewarding journeys in the Himalayas.
              </p>
              <p>
                Each peak represents a different aspect of Lord Shiva's divine presence:
              </p>
              <ul>
                <li><strong>Kinner Kailash:</strong> The sacred peak in Kinnaur, Himachal Pradesh</li>
                <li><strong>Shrikhand Mahadev:</strong> The ancient peak in Kullu, Himachal Pradesh</li>
                <li><strong>Manimahesh Kailash:</strong> The sacred peak near Chamba, Himachal Pradesh</li>
                <li><strong>Adi Kailash:</strong> The remote peak in Uttarakhand, near the Tibet border</li>
                <li><strong>Kailash Mansarovar:</strong> The most sacred peak in Tibet (not included in our tours)</li>
              </ul>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default PanchKailash;